import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../lib/auth.js";
import { consumeAuthCallbackError } from "../lib/authCallbackError.js";
import { normalizeAuPhone } from "../lib/phone.js";
import { Button, Input } from "../lib/ui.js";

/**
 * Password + optional TOTP (docs/architecture.md section 8, updated). Account creation
 * itself is invite-only via supabase/functions/invite-member -- there's no signup form here,
 * only sign-in. Two steps, both rendered from THIS component:
 *   1. Email + password -> signInWithPassword. Establishes a session immediately, at aal1.
 *   2. If the account has a verified TOTP factor, mfaPending (lib/auth.tsx) is now true and
 *      Guards.tsx keeps routing back to /login -- so step 2 renders using the SAME session,
 *      not a fresh one. A user with no factor enrolled skips straight past this.
 *
 * A staff login created by MOBILE number (migration 0060, invite-member) has no password: "Sign in with your mobile" texts a
 * one-time code (Supabase phone sign-in; the auth-sms-hook Edge Function sends it through Texto) and verifies it. Only accounts
 * that already exist can use it (shouldCreateUser: false), so this is not a sign-up path.
 *
 * /login has no guard of its own -- nothing else redirects an already-authenticated visitor
 * away from it, so BOTH success paths below navigate("/") explicitly. Skipping that (a real
 * bug this fixed) left a successfully signed-in user staring at a reset, blank login form
 * with no indication anything happened -- indistinguishable from a dead loop.
 */
export function Login() {
  const { mfaPending, refreshMfaStatus } = useAuth();
  const navigate = useNavigate();
  const initialCallbackError = useState(() => consumeAuthCallbackError())[0];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<"idle" | "working" | "error">(initialCallbackError ? "error" : "idle");
  const [error, setError] = useState<string | null>(initialCallbackError);
  const [resetSent, setResetSent] = useState(false);
  const [byMobile, setByMobile] = useState(false);
  const [mobile, setMobile] = useState("");
  const [mobileE164, setMobileE164] = useState<string | null>(null); // set once a code has been sent
  const [smsCode, setSmsCode] = useState("");

  async function handleSendMobileCode(e: FormEvent) {
    e.preventDefault();
    setStatus("working");
    setError(null);
    const n = normalizeAuPhone(mobile, null);
    if (!n.ok) {
      setStatus("error");
      setError(n.error);
      return;
    }
    const { error: err } = await supabase.auth.signInWithOtp({ phone: n.e164, options: { shouldCreateUser: false } });
    if (err) {
      setStatus("error");
      setError(/signups? not allowed|user.*not.*found/i.test(err.message) ? "No account uses that mobile number. Ask your manager to add it." : err.message);
      return;
    }
    setStatus("idle");
    setMobileE164(n.e164);
  }

  async function handleVerifyMobileCode(e: FormEvent) {
    e.preventDefault();
    if (!mobileE164) return;
    setStatus("working");
    setError(null);
    const { error: err } = await supabase.auth.verifyOtp({ phone: mobileE164, token: smsCode.trim(), type: "sms" });
    if (err) {
      setStatus("error");
      setError(err.message);
      return;
    }
    await refreshMfaStatus();
    setStatus("idle");
    navigate("/", { replace: true });
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("working");
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) {
      setStatus("error");
      setError(err.message);
      return;
    }
    // Checked directly rather than trusting the mfaPending already in context -- that only
    // updates once lib/auth.tsx's own effect reacts to the new session, which hasn't
    // necessarily happened yet at this exact point.
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    await refreshMfaStatus();
    setStatus("idle");
    if (!(aal?.currentLevel === "aal1" && aal?.nextLevel === "aal2")) {
      navigate("/", { replace: true });
    }
    // else: stay on /login -- mfaPending is now true in context, so the render below switches
    // to the code-entry form using this same session.
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus("working");
    setError(null);
    const { data: factors, error: factorsErr } = await supabase.auth.mfa.listFactors();
    const factor = factors?.totp.find((f) => f.status === "verified");
    if (factorsErr || !factor) {
      setStatus("error");
      setError(factorsErr?.message ?? "No authenticator app is set up on this account.");
      return;
    }
    const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: factor.id });
    if (challengeErr) {
      setStatus("error");
      setError(challengeErr.message);
      return;
    }
    const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: factor.id, challengeId: challenge.id, code });
    if (verifyErr) {
      setStatus("error");
      setError(verifyErr.message);
      return;
    }
    await refreshMfaStatus();
    setStatus("idle");
    navigate("/", { replace: true });
  }

  async function handleForgotPassword() {
    if (!email) {
      setError("Enter your email above first.");
      return;
    }
    setStatus("working");
    setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/set-password`,
    });
    setStatus("idle");
    if (err) {
      setError(err.message);
      return;
    }
    setResetSent(true);
  }

  return (
    <div className="rc-auth">
      <div className="rc-auth-panel">
        <div className="rc-auth-brand">
          <span className="rc-mark" aria-hidden="true">
            R
          </span>
          Receptionist
        </div>
        <div className="rc-card" style={{ padding: 28 }}>
          <h1 className="rc-h1" style={{ marginBottom: 4 }}>
            {mfaPending ? "Two-factor check" : resetSent ? "Check your email" : "Sign in"}
          </h1>

          {mfaPending ? (
            <form onSubmit={handleCodeSubmit}>
              <p style={{ fontSize: 14, color: "var(--rc-text-muted)", marginTop: 0 }}>Enter the 6-digit code from your authenticator app.</p>
              <label style={labelStyle}>
                Code
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  style={{ letterSpacing: 4, fontSize: 16 }}
                />
              </label>
              <Button variant="primary" type="submit" disabled={status === "working"} style={submitStyle}>
                {status === "working" ? "Verifying..." : "Verify"}
              </Button>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
            </form>
          ) : resetSent ? (
            <p style={{ color: "var(--rc-text-muted)", marginBottom: 0 }}>We sent a link to {email}. Open it to set a new password.</p>
          ) : byMobile ? (
            mobileE164 ? (
              <form onSubmit={handleVerifyMobileCode}>
                <p style={{ fontSize: 14, color: "var(--rc-text-muted)", marginTop: 0 }}>We texted a 6-digit code to {mobile}.</p>
                <label style={labelStyle}>
                  Code
                  <Input
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    style={{ letterSpacing: 4, fontSize: 16 }}
                  />
                </label>
                <Button variant="primary" type="submit" disabled={status === "working"} style={submitStyle}>
                  {status === "working" ? "Verifying..." : "Sign in"}
                </Button>
                {error && (
                  <p role="alert" style={errorStyle}>
                    {error}
                  </p>
                )}
                <p style={{ marginTop: 16, marginBottom: 0, textAlign: "center" }}>
                  <button type="button" onClick={() => { setMobileE164(null); setSmsCode(""); setError(null); }} style={linkStyle}>
                    Use a different number
                  </button>
                </p>
              </form>
            ) : (
              <form onSubmit={handleSendMobileCode}>
                <p style={{ fontSize: 14, color: "var(--rc-text-muted)", marginTop: 0, marginBottom: 20 }}>We will text you a code to sign in.</p>
                <label style={labelStyle}>
                  Mobile number
                  <Input type="tel" autoComplete="tel" required autoFocus value={mobile} onChange={(e) => setMobile(e.target.value)} />
                </label>
                <Button variant="primary" type="submit" disabled={status === "working"} style={submitStyle}>
                  {status === "working" ? "Sending..." : "Text me a code"}
                </Button>
                {error && (
                  <p role="alert" style={errorStyle}>
                    {error}
                  </p>
                )}
                <p style={{ marginTop: 16, marginBottom: 0, textAlign: "center" }}>
                  <button type="button" onClick={() => { setByMobile(false); setError(null); }} style={linkStyle}>
                    Sign in with email instead
                  </button>
                </p>
              </form>
            )
          ) : (
            <form onSubmit={handlePasswordSubmit}>
              <p style={{ fontSize: 14, color: "var(--rc-text-muted)", marginTop: 0, marginBottom: 20 }}>Use the email and password for your account.</p>
              <label style={labelStyle}>
                Email
                <Input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label style={labelStyle}>
                Password
                <Input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <Button variant="primary" type="submit" disabled={status === "working"} style={submitStyle}>
                {status === "working" ? "Signing in..." : "Sign in"}
              </Button>
              {error && (
                <p role="alert" style={errorStyle}>
                  {error}
                </p>
              )}
              <p style={{ marginTop: 16, marginBottom: 0, textAlign: "center" }}>
                <button
                  type="button"
                  onClick={() => void handleForgotPassword()}
                  style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 600 }}
                >
                  Forgot your password?
                </button>
              </p>
              <p style={{ marginTop: 8, marginBottom: 0, textAlign: "center" }}>
                <button type="button" onClick={() => { setByMobile(true); setError(null); }} style={linkStyle}>
                  Sign in with your mobile instead
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, marginBottom: 14, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)" };
const linkStyle: React.CSSProperties = { background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 600 };
const submitStyle: React.CSSProperties = { width: "100%", padding: "10px 14px", marginTop: 4 };
const errorStyle: React.CSSProperties = { color: "var(--rc-danger)", fontSize: 13.5, margin: "12px 0 0" };
